/*
 * The frontend module contract (modules.md §8). `web/src/modules.ts` is the only file that names a
 * module; everything else goes through the registry, which is why core never imports a module tree
 * and a module can be switched off by deleting one line.
 *
 * A module contributes through this one record and nothing else: routes (lazy, so the shell stays
 * inside its budget), one reducer for its own "<id>.*" events, slot entries that core frames render,
 * a translation namespace, what it `provides` to core's own surfaces, and the `bind` hook the boot
 * calls once the client exists. Nothing here renders and nothing here runs; the shell reads the
 * registry, and `bind.ts` beside it hands each module what the boot built.
 */

import type { AnyRoute } from "@tanstack/react-router"
import type { ComponentType, LazyExoticComponent, ReactNode } from "react"
import type { StoreApi } from "zustand"
import type { Outbox } from "../drafts/index.ts"
import type { ConversationNotifications } from "../notify/overrides.ts"
import type { PresenceReader } from "../people/presence.ts"
import type { SearchResult } from "../search/api.ts"
import type { IconName } from "../shell/icons.tsx"
import type { Applied, ConversationId, Model, WireEventLike } from "../store/model.ts"
import type { ClientStore } from "../store/store.ts"
import type { ReplyData, RequestData, RequestType } from "../wire/index.ts"

/** Widened when a module is added; a string literal union, never `string` (modules.md §8). */
export type ModuleId = "chat" | "aichat" | "drive"

/**
 * A slot entry that may arrive in a chunk of its own: the component itself, or the `lazy()` of it.
 *
 * Four slots take one — a sidebar section's rows, a details view and its aside, and a part of the
 * administrators' page *Open to AI chats* — because a module whose screens live outside the entry
 * chunk cannot contribute a *static* component to a frame: the import that names the component is
 * what pulls the whole area back into the entry, and the shell has 5 KB of headroom for every module
 * there will ever be (`scripts/checkBudgets.ts`). The frames that render these wrap them in a
 * `Suspense`, so a module may hand over either shape and no frame knows which it got. The other
 * slots stay eager: what they draw is a menu row, a hit or a page the reader has already navigated
 * to, and none of them is on screen before anything else is.
 */
export type SlotComponent<P = Record<string, never>> =
	ComponentType<P> | LazyExoticComponent<ComponentType<P>>

/** A module's reducer sees the whole model and returns a new one, exactly like core's. */
export type EventReducer = (model: Model, event: WireEventLike) => { next: Model; result: Applied }

/** One IndexedDB object store, declared into the single upgrade function of core/persist/idb.ts. */
export interface IdbStoreDef {
	name: string
	keyPath: string | string[]
	indexes?: { name: string; keyPath: string | string[]; unique?: boolean }[]
}

/**
 * A group of conversations in the sidebar; the module supplies the rows, core the chrome — the
 * heading row that folds the section, its `+`, the "· n" for what a folded section hides, and the
 * one empty state of a column whose every section is empty (VISUAL.md §7.2).
 *
 * Two of the members are *selectors* over the model rather than components or hooks: a count of
 * hidden rows and the fact of emptiness are facts about the model, and core reads them through one
 * `useModel` per question — a primitive answer, so the heading repaints when the model moves and
 * not otherwise. A module answers them from its own slice the way its own rows do.
 */
export interface SidebarSection {
	id: string
	titleKey: string
	order: number
	/**
	 * The rows. `collapsed` is the heading's folded state (`core.shell.sidebar_collapsed`): a folded
	 * section draws only the rows that must stay visible — the current conversation and every one
	 * carrying a mention of the reader — and nothing else; unfolded it draws them all.
	 */
	Rows: SlotComponent<{ collapsed: boolean }>
	/**
	 * Drawn in the section's heading row, after the title. It is where a section's own controls
	 * belong — the `+` that starts a channel, a filter — because a control that sits under the
	 * heading reads as the first row of the list rather than as something about the list.
	 */
	Actions?: ComponentType
	/**
	 * How many rows the folded render leaves out — the rows that are neither `active` (the
	 * conversation the main pane shows, or null) nor carrying a mention — so the heading can say
	 * "· 3". Read only while the section is folded; a section without it says nothing about what it
	 * hides.
	 */
	hiddenCount?(model: Model, active: ConversationId | null): number
	/**
	 * Whether the section has no rows at all. When every section of a place answers true the column
	 * draws one `ListState.Empty` in their place — not a heading per section over a sentence each —
	 * so neither the headings nor the `Actions` are on screen then; a section that mounts a dialog
	 * inside `Actions` mounts it in `Door` as well. Absent means "never empty".
	 */
	isEmpty?(model: Model): boolean
	/**
	 * The one way out of an empty column: a `primary` button (and whatever dialog it opens) that
	 * creates the first thing this section lists — chat's *Create a channel*. The first section in
	 * `order` that offers one supplies the column's door.
	 */
	Door?: ComponentType
	/**
	 * The place this section belongs to — a top-level destination of the rail, `core/shell/place.ts`
	 * (UX §1.2). A section without one belongs to Home, where the conversations are; the column is
	 * drawn on every place (VISUAL.md §7.13, D15), so a section that names no place is on every
	 * place as well.
	 */
	place?: string
	/**
	 * Claims the column on this section's `place`: there, only sections naming that place are drawn
	 * and every placeless section is left out.
	 *
	 * It exists because a place can have a column of its own to say something with. The AI Chats
	 * column is a find field, the chats waiting for the reader and the reader's own chats, and a list
	 * of AI chats under a list of channels is two navigations in one column — the reader has to work
	 * out which of them the page they are on belongs to. Claiming is the claiming
	 * place's own decision: the alternative was naming a place in *chat's* three sections, which
	 * would empty the column on Activity and Search too.
	 */
	sidebar?: "own"
	/**
	 * Folds the column away on some addresses of this section's place while the window is narrow,
	 * so that the page there keeps its width (`core/shell/place.ts`, `useSidebarFold`). A folded
	 * column comes back over the page from a toggle the page draws, and a person's own choice to
	 * keep it open or folded is remembered on the device and wins over the width.
	 *
	 * It is the section's to say, like the claim above, because only the place knows which of its
	 * screens need the room — AI Chats folds its column beside an open chat and never on its home,
	 * where the column *is* the way in.
	 */
	fold?: SidebarFoldRule
}

/** Where and below which width a place's column folds away (`SidebarSection.fold`). */
export interface SidebarFoldRule {
	/**
	 * The window width, in CSS pixels, from which the column has room beside the page: at and above
	 * it the column stands unless the person folded it themselves.
	 */
	belowPx: number
	/** Whether the column folds on this address at all — an open document, not the tab's home. */
	on(pathname: string): boolean
}

/**
 * One button on Home's empty state (UX §1.2). Home has nothing of its own to show a reader who has
 * joined nothing, so the modules say what there is to do — core draws the welcome and the row, a
 * module supplies the doors.
 */
export interface HomeAction {
	id: string
	labelKey: string
	onSelect(): void
	/** At most one per build; drawn as the primary button, the rest as secondary. */
	primary?: boolean
}

/**
 * One block of Home's catch-up screen (VISUAL.md §7.8). Home itself is core's — the greeting, the
 * count line, the "up to date" and "nothing joined yet" states — and what is waiting is the
 * modules': chat contributes the cards of its unread conversations and the threads with new
 * replies, because what a conversation *is* and what its first unread line says are questions
 * core cannot answer (D-06 §2.5). Sections are drawn in `order` under the greeting.
 *
 * `render` is a function and not a component so that a section with nothing to say costs nothing
 * — it answers `null` and Home draws the empty state from its own reading of the unread counts.
 */
export interface HomeSection {
	id: string
	order: number
	render(): ReactNode
	/**
	 * The door of the administrator's first step (VISUAL.md §7.16): the control that creates the
	 * place everybody joins, drawn in the first row of the setup checklist. It is a component and not
	 * a callback because the step has a form of its own — the step-up field a stale session is asked
	 * for appears *inside* it, never in a dialog over the screen (D-10 D11). `onDone` is what the
	 * screen does once the place exists: dismiss itself. The first section in `order` that offers
	 * one supplies the row's door; a checklist with none says where the control is instead.
	 */
	SetupDoor?: ComponentType<{ onDone: () => void }>
	/**
	 * Whether this section has something waiting for the reader right now. Home says "You're up to
	 * date" only while chat's unread totals are zero **and** no section answers true, so a module
	 * whose waiting things are not conversations — an AI chat with a question for the reader — is
	 * never drawn under a sentence that says nothing is waiting. A selector over the model like the
	 * sidebar's `isEmpty`: Home reads it through `useModel`, and it answers a boolean. Absent means
	 * the section never holds anything back from "up to date".
	 */
	hasItems?(model: Model): boolean
}

/**
 * Which module supplies the setup checklist's first door: the first section in `order` that offers
 * one. Written once, beside the slot it reads, because both screens that draw the checklist ask the
 * same question — Home's catch-up and the administrator's first day.
 */
export function setupDoorOf(
	sections: readonly HomeSection[],
): HomeSection["SetupDoor"] | undefined {
	return [...sections].sort((a, b) => a.order - b.order).find((s) => s.SetupDoor !== undefined)
		?.SetupDoor
}

/**
 * A screen of a module drawn on the whole window, beside the shell rather than inside it — the way
 * the sign-in screens are core's (`routes/routes.tsx` `authRoutes`). It is for a page a person may
 * reach without being signed in, or signed in as somebody this instance does not let in: an AI chat's
 * preview sends its visitors to one, to sign in, to ask for access or to wait while it starts.
 *
 * Such a page is booted without the client: no shell, no socket, no database, no catch-up, with or
 * without a session (`main.tsx`). It reads what it needs over HTTP itself, and it leaves by a full
 * page load — a link or `location.assign` — never by a router navigation, because the client boots
 * once and a page drawn without it has nothing to hand the shell. `main.tsx` holds it to that: a
 * router navigation away from a full page becomes a page load.
 *
 * Its address must be one no signed-in screen of the module claims — a static segment where the
 * module's own routes have a parameter outranks it, which is how `/ai/preview/$key` stands beside
 * `/ai/$key/$rest`.
 */
export interface FullPage {
	/**
	 * The address as a route pattern: static segments and `$name` parameters, one segment each, no
	 * splat (`/ai/preview/$key/starting`).
	 */
	path: string
	/**
	 * The screen, as a chunk of its own: an `import()` named by a literal specifier, so that nothing of
	 * it reaches the entry chunk. It is asked for while the bundle is still being evaluated when the
	 * document was opened at its address, and the router asks again (the second `import()` is free).
	 */
	load(): Promise<ComponentType<FullPageProps>>
}

/** What a full page is handed: its address, already taken apart. */
export interface FullPageProps {
	/** The pattern's parameters by name, decoded (`{ key: "h7kt9qc3" }`). */
	params: Readonly<Record<string, string>>
	/**
	 * The query string as it arrived. A page reads its own values from it, never through the router's
	 * search parsing, which reads a value as JSON and so turns `?r=123` into a number.
	 */
	search: URLSearchParams
}

/**
 * A module's page for somebody without a session at one of its addresses (DV W-1 §3.7, S-01 §3.20):
 * a document opened by its link by a visitor, who has no account. `main.tsx` asks for the session
 * first, and only when it answers nobody does an address that `matches` draw this page in place of
 * the sign-in — with neither the shell nor the signed-in client: no rail, no device claimed, no
 * database, no catch-up. Unlike a full page it does open a socket, the visitor's own (`core/net/
 * socket.ts`'s `visitor` option), which the page dials itself.
 *
 * The address decides by its shape, a document's link id where the module's routes take a key too,
 * which a route pattern cannot tell apart; hence a predicate rather than a pattern. It leaves by a
 * page load, as a full page does.
 */
export interface VisitorPage {
	/** Whether the page answers this address for somebody without a session. */
	matches(pathname: string): boolean
	/** The page, as a chunk of its own, asked for once the session has answered nobody. */
	load(): Promise<ComponentType<VisitorPageProps>>
}

/** What a visitor's page is handed: where it was opened, and the build it is. */
export interface VisitorPageProps {
	pathname: string
	/** The query string as it arrived (`?tab=`), read by the page itself, as a full page's is. */
	search: URLSearchParams
	/** `web/<sha>`, the build the page's connection names in its hello. */
	build: string
}

/**
 * How one activity kind reads, written by the module that owns the kind (S-11-D7).
 *
 * Core's feed builds every row it recognises around the conversation the row happened in, and four
 * kinds of this product happen in none: a change to a document is in a file. So the module that
 * writes the item says how its row reads and where it leads, from the item's own payload — which is
 * the module's shape and opaque to core — and core keeps the row: the face, the time, the unread
 * bar, the triage keys.
 *
 * Both answers are allowed to give up. A payload written by a newer build, a key this build does
 * not ship: `line` answers "" and the feed falls back to what it can say for itself, and `href`
 * answers null and the row is a row without a link rather than a link to nowhere.
 */
export interface ActivityRowView {
	/**
	 * The row's sentence. `actorIds` are the people the row stands for, newest first, and `nameOf`
	 * resolves one to a display name — the module never reads core's account slice for this.
	 */
	line(data: unknown, actorIds: readonly number[], nameOf: (id: number) => string): string
	/** Where the row leads, as an in-application path. */
	href(data: unknown): string | null
	/**
	 * Present on a kind that is worth a desktop banner while the window is not being looked at
	 * (`core/notify/desktop.ts`). The banner's body is `line` and its click `href`, so the feed and
	 * the banner never say two different things about one row.
	 */
	desktop?: ActivityDesktop
	/**
	 * What kind of thing this row is, in the module's own words for this one row — a plan and a
	 * request to change code are not "a question" — written for the middle of a sentence, so the
	 * feed never lower-cases it. It names the row's read button. A module that gives it writes
	 * sentences that already say what happened, so the feed draws no kind after them. Absent, the
	 * title the kind registered with the server stands in, lower-cased.
	 */
	label?(data: unknown): string
	/**
	 * The glyph drawn in the face's place when the row names no person — what Claude did, what a
	 * check found. Absent, such a row draws nobody's empty face.
	 */
	face?: IconName
}

/**
 * How one activity kind banners on the desktop. Core decides *whether* a banner is shown — the
 * account's preference, the platform's permission, the window not being looked at — and the module
 * says what it is called and which earlier banner it replaces.
 */
export interface ActivityDesktop {
	/** The banner's title: what kind of thing happened ("Claude asks you something"). */
	title(data: unknown): string
	/**
	 * The platform's tag: a banner with the same tag replaces the one before it, so twenty rows about
	 * one chat are one banner that updates. The module names the scope, because only it knows what
	 * the row's opaque `data` is about.
	 */
	tag(data: unknown): string
	/**
	 * Whether a *raised* row banners again. A row that coalesced a second occurrence into itself is
	 * news the reader already had, so by default only a new row banners; the few kinds whose raise is
	 * the news — an escalation, a preview that moved to a newer revision — say so here.
	 */
	onRaise?: boolean
}

/**
 * Renders one search hit of a SearchKind the module owns (UX §8 #10). The hit itself is handed over
 * whole, because what makes a hit readable is what only its own module can draw — who wrote it,
 * with which face — and the excerpt the server cut is part of the same row. The address stays
 * core's: `SearchLocator` is the one place that knows this build's URL shapes, and a module that
 * built its own href would answer a different one from the page around it.
 */
export type SearchResultRenderer = ComponentType<{
	result: SearchResult
	/** Where the hit lives, or null when this build cannot address that kind of hit. */
	href: string | null
	/** In-place navigation for the row's link; without it the link is an ordinary one. */
	onNavigate?: ((path: string) => void) | undefined
}>

export interface SettingsPage {
	path: string
	titleKey: string
	Page: ComponentType
}

export interface AdminPage {
	path: string
	titleKey: string
	Page: ComponentType
}

/**
 * One part of Administration ▸ *Open to AI chats* (A-4 A4-D8, §7): what of one module Claude can read
 * in AI chats beyond each chat's own — the documents by their level, the channels opened to AI chats,
 * the AI chats closed to the others. The frame is core's and knows no module; each module lists its
 * own from its own administrators' route, read-only, so the page changes nothing and every switch
 * stays where it lives. `order` places the part on the page, which reads documents, then channels,
 * then AI chats whatever order `modules.ts` lists the modules in. The part is a chunk of its own:
 * nobody but an administrator who opens the page ever fetches it.
 */
export interface AgentReachPart {
	id: string
	order: number
	Part: SlotComponent
}

/**
 * How a module names one kind of work on the administrator's AI worker page (S-06 §5).
 *
 * A worker machine is granted kinds of work and runs work sessions of them, and the server knows
 * both only by the id the two machines exchange — "aichat.chat", "aichat.auth" — which core cannot
 * put into words of its own. The module that owns the kind names it, so the page says what the work
 * is rather than the id; a kind no module of this build names is printed as it is.
 */
export interface WorkSubject {
	/** The kind this names, exactly as the server spells it, e.g. "aichat.chat". */
	kind: string
	/** What the kind is, in the words of the people using this instance; a key of the module's. */
	labelKey: string
}

/**
 * One thing the details column can show (UX §2.1). It is opened by a request naming its id — a
 * thread, a profile, a conversation's details — and never by being registered: the column is empty
 * until somebody asks for one of these.
 */
export interface DetailsView {
	/** What a `DetailsRequest.view` names, e.g. "chat.thread". */
	id: string
	/** The view's own name: the dialog's title below 1200 px, and the pane's heading when the view has no group title. */
	titleKey: string
	/**
	 * What the column is headed while this view is open: the view *group* rather than the tab —
	 * "Conversation details" over About, Files and Pinned; "Profile"; "Thread" (VISUAL.md §7.7,
	 * D-06 S6). Falls back to `titleKey`. The tabs under the heading carry the short names.
	 */
	groupTitleKey?: string
	/**
	 * The name on this view's tab, where it has siblings. A tab strip is read as a row of choices
	 * and has one column to fit in, so the tabs are short — "About" beside the heading's
	 * "Conversation details" — and a view that gives none is named by its heading.
	 */
	tabLabelKey?: string
	View: SlotComponent<{ params: Record<string, string | number> }>
	/**
	 * A view's own chrome beside the heading, inside the pane's 48 px header row — the thread's
	 * place tag ("in #design"). Drawn after the `h2` and before the close button; absent, the
	 * header holds the heading alone.
	 */
	Aside?: SlotComponent<{ params: Record<string, string | number> }>
	/**
	 * Views that are tabs of one another share a group — "chat.conversation" for About, Files and
	 * Pinned: the pane draws a strip of the group's views when two or more of them answer
	 * `relevant` for the open request's params, and a tab click keeps those params and swaps the
	 * view. A view without a group is shown on its own.
	 */
	group?: string
	/** The strip's order, ascending; ties fall back to the id. */
	order?: number
	/** Whether this view has something to show for these params; absent means always. */
	relevant?(params: Record<string, string | number>): boolean
}

/** One keyboard binding, registered into the single shortcut registry (A31). */
export interface ShortcutDef {
	id: string
	keys: string[]
	/**
	 * Where the binding is live: one of core's scopes (`CORE_SCOPE_STACKS`,
	 * `core/shell/shortcuts.ts`) or one a module declares in the `shortcutScopes` slot.
	 */
	scope: string
	labelKey: string
	run(): void
	remappable?: boolean
	/**
	 * What the provider says out loud when this binding runs (`core/shell/shortcuts.ts`
	 * `runShortcut`): a catalogue key, for a chord whose effect a screen reader cannot notice. A
	 * binding that shows a toast carries none — a toast speaks for itself.
	 */
	announceKey?: string
}

/**
 * A scope of a module's own page — a document, an AI chat, a review's code — which the page pushes
 * while it is open (`useShortcutScope`). Core knows its own scopes; a module's arrive here, with the
 * heading the keyboard sheet draws over their bindings and the stack they live on, which is what the
 * collision check reads to decide whether two bindings on one chord can ever both be live.
 */
export interface ShortcutScopeDef {
	/** The name the module's bindings carry in `ShortcutDef.scope`. */
	scope: string
	/** The sheet's heading over this scope's bindings, a key of the module's catalogue. */
	titleKey: string
	/**
	 * Every scope open together with this one, innermost last, this one included — core's `global`
	 * and `composer` among them where they are. Two scopes of one page name the same stack.
	 */
	stack: readonly string[]
}

/**
 * Feeds the quick switcher (Ctrl/Cmd+K) with the module's own things to jump to.
 *
 * A row may carry how much is waiting behind it — `count` unread, `mention` when some of it names the
 * reader — because somebody pressing Ctrl+K is usually going to what is unread (X-B1 A2), and only the
 * module knows: the unread cursors live in the module's own slice, and core may not read a module's
 * state. Core's own rows (Activity, Settings, a person) carry neither, which is right.
 */
export interface QuickSwitcherProvider {
	id: string
	query(term: string): Promise<
		{
			id: string
			label: string
			detail?: string
			to: string
			userId?: number
			count?: number
			mention?: boolean
			/**
			 * The heading a run of these rows stands under, as a key of this module's own catalogue.
			 * Core's groups are conversations, people and pages; a module that brings a third kind of
			 * destination says what to call it, and its runs are drawn after core's.
			 */
			groupTitleKey?: string
			/** The glyph of the row, where core cannot recognise what it leads to; an `IconName`. */
			icon?: string
		}[]
	>
}

/** An AI chat as a byline names it to a reader of the chat: its title, and where it opens. */
export interface ChatLabel {
	title: string
	href: string
}

/**
 * The AI chats a byline may name for this reader, by chat id (A-4 A4-D9): "Claude, asked by Ben in
 * *Pricing rework*" to a reader of the chat, "Claude, asked by Ben" to anybody else. The module that
 * owns AI chats answers from what the server told this reader — the chats they read — so a chat it
 * does not hold is one the byline leaves out, never one it guesses. The answer keeps its identity
 * while the chats it reads from do, so a frame may select it from the model.
 */
export type ChatLabelSource = (model: Model) => ReadonlyMap<number, ChatLabel>

/**
 * Every place a core frame lets a module put something (modules.md §8). A frame reads a slot through
 * `useSlot`, which hands it the contributions of the modules this reader may use right now
 * (`core/modules/slots.ts`), so no frame repeats that rule.
 */
export interface Slots {
	sidebarSections: SidebarSection[]
	homeActions: HomeAction[]
	homeSections: HomeSection[]
	searchResultRenderers: Record<number, SearchResultRenderer>
	settingsPages: SettingsPage[]
	adminPages: AdminPage[]
	agentReach: AgentReachPart[]
	workSubjects: WorkSubject[]
	detailsViews: DetailsView[]
	shortcuts: ShortcutDef[]
	shortcutScopes: ShortcutScopeDef[]
	quickSwitcherProviders: QuickSwitcherProvider[]
	chatLabels: ChatLabelSource[]
}

/** One typed socket request. It rejects while the socket is not open, as every send does. */
export type Request = <T extends RequestType>(
	type: T,
	data: RequestData<T>,
) => Promise<ReplyData<T>>

/**
 * What the boot hands a module once the store, the outbox and the socket's request exist
 * (`FrontendModule.bind`). It is built before the first render and before the socket is up: a
 * request made before then is rejected the way one made while the socket is down is, which is the
 * case the outbox already handles by keeping the item.
 */
export interface ModuleBindings {
	client: ClientStore
	outbox: Outbox
	request: Request
}

/**
 * What a module offers core's own surfaces, where the surface is core's and the knowledge the
 * module's (`FrontendModule.provides`). Each is offered by at most one module of a build, and a
 * build in which none offers it draws the surface without it.
 */
export interface ModuleProvides {
	/** Who is here right now, for every face core draws (`core/people/presence.ts`). */
	presence: PresenceReader
	/** The per-conversation rows of Settings ▸ Notifications (`core/notify/overrides.ts`). */
	conversationNotifications: ConversationNotifications
	/**
	 * The address of the trash's own search for a term (DS-3 §3.3): the Search page's Drive section
	 * links to it as *Search the trash* under its deleted items, the one door to the older trash.
	 */
	trashSearch: (term: string) => string
}

export interface FrontendModule {
	id: ModuleId
	/**
	 * Lazy route bundle: TanStack routes mounted under the shell, each on the module's own path.
	 * The parent is handed in because a module never imports the router tree it hangs under
	 * (modules.md §8) — the shell owns that tree and passes it at load time.
	 */
	routes: (parent: AnyRoute) => Promise<AnyRoute[]>
	/** applyEvent cases for every "<id>.*" event type; exhaustive over the module's own union. */
	reducer: EventReducer
	/** Slot contributions rendered by core frames; each entry is typed by the slot it fills. */
	slots?: Partial<Slots>
	/**
	 * How this module's activity kinds read in the feed, by kind (S-11-D7). It is beside the slots
	 * and not one of them: the slots are places where a module draws something, and this is two
	 * sentences about a row core draws itself. A kind with no entry is read by core's own shapes,
	 * which is what every chat kind is.
	 */
	activityRows?: Record<string, ActivityRowView>
	/**
	 * The words on the Activity feed's filter chip of each renderer group this module registers its
	 * kinds under, as keys of its own catalogue ("aichat" → *AI chats*). The chips follow the
	 * server's table of kinds; a group without words here is drawn under its own name.
	 */
	activityGroups?: Record<string, string>
	/**
	 * Whether this reader may use the module at all, where the answer is narrower than the standing:
	 * AI chats can be limited to one group's team members (`[modules.aichat] members_group`), which
	 * the server says in the module's own bootstrap. The rail leaves out a `teamOnly` place of a
	 * module that answers false (`core/shell/Rail.tsx`). A selector over the model; absent means
	 * every reader the place's own rules admit.
	 */
	admits?(model: Model): boolean
	/**
	 * The module's full pages: screens drawn on the whole window without the shell or the client,
	 * reachable signed out (`FullPage`). Beside the slots like `activityRows`, because a core frame
	 * draws nothing around them.
	 */
	fullPages?: FullPage[]
	/**
	 * The module's pages for somebody without a session at one of its addresses (`VisitorPage`): what
	 * a visitor by link meets where a member meets the module's own screen.
	 */
	visitorPages?: VisitorPage[]
	/**
	 * What this module offers core's own surfaces (`ModuleProvides`). The boot installs every offer
	 * before the first render (`installProvisions`, `core/modules/bind.ts`), so a face drawn on the
	 * first paint already asks the module for the dot beside it.
	 */
	provides?: Partial<ModuleProvides>
	/**
	 * Binds the module to the running client: its outbox transports, its socket listeners, the calls
	 * behind its actions. The boot calls it once, after the outbox exists and before anything renders
	 * (`bindModules`), and never where a full page is drawn. It answers the unbinding of whatever it
	 * registered.
	 */
	bind?(b: ModuleBindings): () => void
	/** One translation namespace, lazily loaded; every key starts with "<id>.". */
	i18n: () => Promise<Record<string, string>>
	/** The IndexedDB stores this module owns. */
	idbStores?: IdbStoreDef[]
	/**
	 * Called once per boot with this module's entry of the bootstrap's `modules` map — raw JSON the
	 * module parses itself, because core does not know its shape (S0-PLAN R-O).
	 */
	onBootstrap?(payload: unknown, store: StoreApi<Model>): void
	/**
	 * This module's stored slice as a new visit must see it: the warm boot hands over whatever was
	 * written to disk and takes back what belongs in `model.modules[<id>]` (`core/persist`).
	 *
	 * It exists because a stored slice is a *cache of a shape that keeps moving*. Core restores the
	 * value verbatim and knows nothing about it, so a field the module has added since the write
	 * reaches a render as `undefined` where every reader expects a record, and a field that belongs to
	 * one visit — chat's unread anchor, pinned again the moment the reader arrives — comes back a day
	 * late. `MODEL_VERSION` is the backstop and not the answer: bumping it throws away every reader's
	 * whole model for one field. A module that answers this restores its own empty slice and copies
	 * what the cache legitimately holds onto it; one that does not is restored as it was written.
	 */
	hydrate?(stored: unknown): unknown
	/**
	 * The other direction: what of this module's slice is written to disk at all. Core rewrites a
	 * module's row whenever the slice changes identity, and it cannot know which of the fields inside
	 * are worth a disk — so a slice that also carries what is true for one second (who is typing, who
	 * is online) or what the server hands over again anyway (a loaded thread and its render trees) is
	 * structure-cloned into IndexedDB on every one of those frames, continuously, while anybody is
	 * working.
	 *
	 * A module that answers this hands back the part of its slice a new visit should start from; the
	 * answer is also what the next save is compared against, so a change that leaves the projection
	 * alone writes nothing at all. A module that does not is written whole, as before.
	 *
	 * It is the counterpart of `hydrate` and needs no version bump to be *narrowed*, because a field
	 * that stops being written is a field the cache does not carry — which is the case `hydrate`
	 * exists for.
	 */
	dehydrate?(slice: unknown): unknown
}

export interface ModuleRegistry {
	byId(id: ModuleId): FrontendModule
	/** "chat.message.new" → chat's reducer; undefined → the event is "unknown". */
	reducerFor(type: string): EventReducer | undefined
	/**
	 * The entries for one slot, concatenated in modules.ts order: every module's, or only those of
	 * the modules `among` names. The answer is memoised per slot and set, so a frame that reads it on
	 * every render keeps one identity.
	 */
	slots<K extends keyof Slots>(k: K, among?: readonly ModuleId[]): Slots[K]
	/** The one module's offer of `k`, or undefined in a build where no module makes it. */
	provided<K extends keyof ModuleProvides>(k: K): ModuleProvides[K] | undefined
	/** How an activity kind reads, from the module that owns it; undefined for core's own shapes. */
	activityRow(kind: string): ActivityRowView | undefined
	all(): readonly FrontendModule[]
}

/** Every module's full pages, in modules.ts order; `registerModules` has already checked them. */
export function fullPagesOf(registry: ModuleRegistry): FullPage[] {
	return registry.all().flatMap((mod) => mod.fullPages ?? [])
}

/**
 * The shape of a full page's address: one or more segments, each a lower-case static word or a
 * `$name` parameter. A splat is left out on purpose — `main.tsx` decides from the address alone,
 * before the router exists, whether the document is a full page, and a segment-for-segment match is
 * the whole of what that decision needs.
 */
const FULL_PAGE_PATH = /^(\/([a-z0-9][a-z0-9._-]*|\$[A-Za-z][A-Za-z0-9]*))+$/

/**
 * Whether an address is the one a full page's pattern names: segment for segment, a parameter
 * taking any one non-empty segment, a static word compared without regard to case (as the router
 * compares it), and a trailing slash ignored.
 */
export function matchesFullPage(pattern: string, pathname: string): boolean {
	const want = pattern.split("/").filter((segment) => segment !== "")
	const got = pathname.split("/").filter((segment) => segment !== "")
	if (want.length !== got.length) return false
	return want.every((segment, i) => {
		const part = got[i] ?? ""
		return segment.startsWith("$") ? part !== "" : segment === part.toLowerCase()
	})
}

/** The full page an address belongs to, if any. */
export function fullPageAt(pages: readonly FullPage[], pathname: string): FullPage | undefined {
	return pages.find((page) => matchesFullPage(page.path, pathname))
}

/** The visitor's page an address belongs to, if any module has one there (`VisitorPage`). */
export function visitorPageAt(registry: ModuleRegistry, pathname: string): VisitorPage | undefined {
	return registry
		.all()
		.flatMap((mod) => mod.visitorPages ?? [])
		.find((page) => page.matches(pathname))
}

/** Merges one slot across modules: arrays concatenate in order, records merge with a last wins. */
function mergeSlot<K extends keyof Slots>(mods: FrontendModule[], k: K): Slots[K] {
	const parts = mods.map((m) => m.slots?.[k]).filter((v) => v !== undefined)
	if (parts.length === 0) return (Array.isArray(EMPTY_SLOTS[k]) ? [] : {}) as Slots[K]
	if (Array.isArray(parts[0])) return (parts as unknown[][]).flat() as Slots[K]
	return Object.assign({}, ...parts) as Slots[K]
}

/** The shape of an empty contribution per slot; also what `slots()` answers for no module. */
const EMPTY_SLOTS: Slots = {
	sidebarSections: [],
	homeActions: [],
	homeSections: [],
	searchResultRenderers: {},
	settingsPages: [],
	adminPages: [],
	agentReach: [],
	workSubjects: [],
	detailsViews: [],
	shortcuts: [],
	shortcutScopes: [],
	quickSwitcherProviders: [],
	chatLabels: [],
}

/**
 * Freezes the module list into the registry the shell reads. Two modules with the same id, and two
 * modules offering core the same thing, are programming errors, not runtime conditions: the list is
 * written by hand in one file.
 */
export function registerModules(mods: FrontendModule[]): ModuleRegistry {
	const byId = new Map<ModuleId, FrontendModule>()
	for (const mod of mods) {
		if (byId.has(mod.id)) throw new Error(`duplicate frontend module ${mod.id}`)
		byId.set(mod.id, mod)
	}
	const provided: Partial<ModuleProvides> = {}
	for (const mod of mods) {
		for (const [k, offer] of Object.entries(mod.provides ?? {})) {
			if (offer === undefined) continue
			if (k in provided) throw new Error(`two frontend modules provide ${k}`)
			Object.assign(provided, { [k]: offer })
		}
	}
	const fullPaths = new Set<string>()
	for (const page of mods.flatMap((mod) => mod.fullPages ?? [])) {
		if (!FULL_PAGE_PATH.test(page.path)) throw new Error(`malformed full page path ${page.path}`)
		// Two pages on one pattern would leave the router to pick one; the list is written by hand.
		if (fullPaths.has(page.path)) throw new Error(`duplicate full page ${page.path}`)
		fullPaths.add(page.path)
	}
	const slotCache = new Map<string, unknown>()
	return {
		byId(id) {
			const mod = byId.get(id)
			if (!mod) throw new Error(`unknown frontend module ${id}`)
			return mod
		},
		reducerFor(type) {
			// An event type is "<owner>.<rest>"; without a dot it names no owner, and a malformed
			// type must resolve to nothing rather than to the module whose id it happens to prefix.
			const dot = type.indexOf(".")
			if (dot < 0) return undefined
			return byId.get(type.slice(0, dot) as ModuleId)?.reducer
		},
		slots(k, among) {
			const key = among === undefined ? k : `${k}:${among.join(",")}`
			if (!slotCache.has(key)) {
				const from = among === undefined ? mods : mods.filter((mod) => among.includes(mod.id))
				slotCache.set(key, mergeSlot(from, k))
			}
			return slotCache.get(key) as Slots[typeof k]
		},
		provided(k) {
			return provided[k]
		},
		activityRow(kind) {
			// By the kind's owner and not by a search through every module: a kind is
			// "<owner>.<rest>", and two modules may not claim one another's rows.
			const dot = kind.indexOf(".")
			if (dot < 0) return undefined
			return byId.get(kind.slice(0, dot) as ModuleId)?.activityRows?.[kind]
		},
		all() {
			return mods
		},
	}
}
